import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Landmark, HeartPulse, Truck, GraduationCap, ShoppingCart, Factory,
  ChevronRight, Check
} from 'lucide-react';

const industries = [
  {
    id: 'finance',
    name: 'Finance',
    icon: Landmark,
    title: 'FinTech & Financial Services',
    paragraph: 'MediaLabs delivers AI-accelerated, Version 1 software products and solutions to the Finance sector, with our expertise including:',
    solutions: [
      'Custom Trading & Portfolio Dashboard Platforms',
      'Automated KYC/AML Compliance Workflows',
      'Real-Time Fraud Detection & Analytics Engines',
    ],
    accent: 'blue',
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    icon: HeartPulse,
    title: 'HealthTech & Digital Health',
    paragraph: 'MediaLabs delivers AI-accelerated, Version 1 software products and solutions to the Healthcare sector, with our expertise including:',
    solutions: [
      'Patient Portal & Telehealth Platforms',
      'AI-Powered Diagnostic Triage Systems',
      'HIPAA-Compliant Data Pipeline Automation',
    ],
    accent: 'orange',
  },
  {
    id: 'logistics',
    name: 'Logistics',
    icon: Truck,
    title: 'Supply Chain & Logistics',
    paragraph: 'MediaLabs delivers AI-accelerated, Version 1 software products and solutions to the Logistics sector, with our expertise including:',
    solutions: [
      'Real-Time Fleet Tracking & Route Optimization',
      'Automated Warehouse Management Systems',
      'Predictive Demand Forecasting Dashboards',
    ],
    accent: 'blue',
  },
  {
    id: 'education',
    name: 'Education',
    icon: GraduationCap,
    title: 'EdTech & Learning Platforms',
    paragraph: 'MediaLabs delivers AI-accelerated, Version 1 software products and solutions to the Education sector, with our expertise including:',
    solutions: [
      'AI-Adaptive Learning Management Systems',
      'Interactive Assessment & Grading Platforms',
      'Student Analytics & Engagement Dashboards',
    ],
    accent: 'orange',
  },
  {
    id: 'ecommerce',
    name: 'E-Commerce',
    icon: ShoppingCart,
    title: 'E-Commerce & Retail Tech',
    paragraph: 'MediaLabs delivers AI-accelerated, Version 1 software products and solutions to the E-Commerce sector, with our expertise including:',
    solutions: [
      'Custom Storefront & Checkout Experiences',
      'AI-Driven Product Recommendation Engines',
      'Inventory & Order Lifecycle Automation',
    ],
    accent: 'blue',
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: Factory,
    title: 'Smart Manufacturing & IoT',
    paragraph: 'MediaLabs delivers AI-accelerated, Version 1 software products and solutions to the Manufacturing sector, with our expertise including:',
    solutions: [
      'IoT Sensor Dashboard & Alerting Systems',
      'Predictive Maintenance & Quality Control AI',
      'Production Line Scheduling Automation',
    ],
    accent: 'orange',
  },
];

/* Geometric orbital cage for the icon */
const GeometricFrame = ({ Icon, accent }) => (
  <div className="relative w-full aspect-square max-w-[320px] mx-auto flex items-center justify-center">
    {/* Outer orbit */}
    <motion.div
      className="absolute inset-0 rounded-full border-2 border-dashed"
      style={{ borderColor: accent === 'blue' ? 'rgba(30,172,238,0.2)' : 'rgba(246,141,44,0.2)' }}
      animate={{ rotate: 360 }}
      transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
    />
    {/* Middle orbit */}
    <motion.div
      className="absolute inset-6 sm:inset-8 rounded-full border border-dashed"
      style={{ borderColor: accent === 'blue' ? 'rgba(30,172,238,0.15)' : 'rgba(246,141,44,0.15)' }}
      animate={{ rotate: -360 }}
      transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
    />
    {/* Inner orbit */}
    <motion.div
      className="absolute inset-12 sm:inset-16 rounded-full border border-dashed"
      style={{ borderColor: accent === 'blue' ? 'rgba(30,172,238,0.1)' : 'rgba(246,141,44,0.1)' }}
      animate={{ rotate: 360 }}
      transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
    />
    {/* Glow */}
    <div
      className="absolute inset-12 sm:inset-16 rounded-full blur-2xl"
      style={{ background: accent === 'blue' ? 'rgba(30,172,238,0.08)' : 'rgba(246,141,44,0.08)' }}
    />
    {/* Orbital dots */}
    {[0, 90, 180, 270].map((deg, i) => (
      <motion.div
        key={deg}
        className="absolute w-2.5 h-2.5 rounded-full"
        style={{
          background: (i % 2 === 0) ? '#1EACEE' : '#F68D2C',
          top: '50%',
          left: '50%',
        }}
        animate={{
          x: [Math.cos((deg * Math.PI) / 180) * 100, Math.cos(((deg + 360) * Math.PI) / 180) * 100],
          y: [Math.sin((deg * Math.PI) / 180) * 100, Math.sin(((deg + 360) * Math.PI) / 180) * 100],
          opacity: [0.4, 0.9, 0.4],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'linear', delay: i * 0.5 }}
      />
    ))}
    {/* Center icon */}
    <motion.div
      className="relative z-10 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center shadow-[var(--shadow-soft)]"
      style={{ background: accent === 'blue' ? 'rgba(30,172,238,0.08)' : 'rgba(246,141,44,0.08)' }}
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
    >
      <Icon
        className="w-10 h-10 sm:w-12 sm:h-12"
        style={{ color: accent === 'blue' ? '#1EACEE' : '#F68D2C' }}
        strokeWidth={1.5}
      />
    </motion.div>
  </div>
);

export const IndustriesSection = () => {
  const [activeId, setActiveId] = useState('finance');
  const active = industries.find((i) => i.id === activeId);

  return (
    <section
      id="industries"
      className="py-16 sm:py-20 lg:py-24 border-t border-[color:var(--hairline)]"
      data-testid="industries-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          className="text-center mb-10 sm:mb-14"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="text-xs font-semibold text-brand-blue uppercase tracking-wider">
            Vertical Expertise
          </span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            Expertise Across Industries
          </h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-lg mx-auto">
            Proven AI-powered delivery across the industries that need speed most.
          </p>
        </motion.div>

        {/* Tab bar - horizontal scrollable */}
        <div className="mb-10 sm:mb-12 overflow-x-auto scrollbar-hide -mx-4 px-4">
          <div className="flex gap-2 min-w-max justify-center" data-testid="industries-tab-bar">
            {industries.map((industry) => {
              const Icon = industry.icon;
              const isActive = industry.id === activeId;
              return (
                <button
                  key={industry.id}
                  onClick={() => setActiveId(industry.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors transition-shadow duration-200 whitespace-nowrap ${
                    isActive
                      ? 'bg-brand-blue text-white shadow-[var(--shadow-soft)]'
                      : 'bg-white text-muted-foreground border border-[color:var(--hairline)] hover:bg-secondary/60 hover:text-foreground'
                  }`}
                  data-testid={`industry-tab-${industry.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {industry.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dynamic content */}
        <AnimatePresence mode="wait">
          {active && (
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="grid md:grid-cols-2 gap-8 lg:gap-14 items-center"
              data-testid="industries-content"
            >
              {/* Left - Geometric frame with icon */}
              <div className="flex justify-center">
                <GeometricFrame Icon={active.icon} accent={active.accent} />
              </div>

              {/* Right - Content */}
              <div>
                <h3
                  className="text-xl sm:text-2xl font-bold tracking-tight mb-4"
                  style={{ color: active.accent === 'blue' ? '#1EACEE' : '#F68D2C' }}
                  data-testid="industries-active-title"
                >
                  {active.title}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                  {active.paragraph}
                </p>
                <ul className="space-y-3 mb-8">
                  {active.solutions.map((sol, i) => (
                    <li key={sol} className="flex items-start gap-3">
                      <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-brand-blue/10 flex items-center justify-center">
                        <Check className="w-3 h-3 text-brand-blue" strokeWidth={3} />
                      </span>
                      <span className="text-sm font-medium text-foreground">{sol}</span>
                    </li>
                  ))}
                </ul>
                <motion.a
                  href="#schedule"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-orange hover:text-brand-orange-dark transition-colors duration-200 group"
                  whileHover={{ x: 2 }}
                  data-testid="industries-cta-link"
                >
                  Explore Solutions
                  <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </motion.a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
